import { transformFlowToStages } from '@/features/boe-task/-query/transformers';
import type { ApprovalTaskConfigFlow } from '@/features/boe-task/components/ApprovalTaskConfig/ApprovalTaskConfig';
import { adjustFlowToStageCount } from '@/features/boe-task/components/ApprovalTaskConfig/utils/flowGenerator';
import type { FreeFormQuestionJson } from '@/features/boe-task/types/free-form';
import { mapConfigQuestionsToJson, mapJsonQuestionsToConfigQuestions } from '@/features/boe-task/utils/free-form-mappers';
import { DEFAULT_IN_HOUSE_PACKAGE_DISPLAY_NAME } from '../constants';
import type { PackagePatternDraftConfig, PackagePatternDraftStatus, PatternDraftQuestion } from '../types/pattern-draft-config';
export type { FreeFormQuestionJson } from '@/features/boe-task/types/free-form';

/**
 * BOE パッケージ下書きの editingContent（PatternSetting 相当の宣言形 JSON）。
 *
 * NOTE: schema.d.ts では editingContent が `Record<string, never>`（型生成されない）ため、
 * backend の DraftPatternSettingJson（draft-pattern-setting-codec）に合わせて frontend で型を定義する。
 * frontend は freeForm.questions と approvalTask（段階・担当グループ）を編集する。
 * Flow / Task Entity ID は publish 後に生成されるが、下書き中の承認段階は approvalStages 拡張フィールドに保持する。
 */
export type DraftPatternSettingJson = {
  name: string;
  description?: string;
  anchorDateLabel: string;
  anchorDateLabelCode?: string;
  operationPackageType: string;
  allowIndividualRequest: boolean;
  steps?: DraftStepJson[];
};

/** 未編集フィールド（assignRules 等）は保持のため緩く型付けする。 */
type DraftStepJson = {
  nodes?: DraftNodeJson[];
  [key: string]: unknown;
};

/** 下書き editingContent 内の承認段階（FE 拡張。publish 後は approval-flow API へ反映）。 */
export type DraftApprovalStageJson = {
  stageIndex: number;
  peopleGroupId: string;
  peopleGroupName?: string;
  requiredApprovals?: number;
  designationMode?: 'none' | 'optional' | 'required';
};
type DraftApprovalTaskJson = {
  requiredApprovals: number;
  operationApprovalTaskCatalogCode: string;
  /**
   * node レベルの指名モード（BE の publish コーデックが読み取り
   * `operation_engine_node.designation_mode` に確定させる）。DC-8217 A案。
   */
  designationMode?: 'none' | 'optional' | 'required';
  approvalStages?: DraftApprovalStageJson[];
  [key: string]: unknown;
};
type DraftNodeJson = {
  submitTask?: {
    freeForm?: {
      name: string;
      description: string | null;
      questions: FreeFormQuestionJson[];
    };
    [key: string]: unknown;
  };
  approvalTask?: DraftApprovalTaskJson;
  [key: string]: unknown;
};
const DEFAULT_APPROVAL_FLOW: ApprovalTaskConfigFlow = {
  id: 'start-default',
  type: 'start',
  children: [{
    id: 'end-default',
    type: 'end'
  }]
};
export function hasApprovalTaskInDraft(content: DraftPatternSettingJson): boolean {
  return findApprovalTaskNode(content) != null;
}
function findApprovalTaskNode(content: DraftPatternSettingJson): DraftNodeJson | undefined {
  for (const step of content.steps ?? []) {
    for (const node of step.nodes ?? []) {
      if (node.approvalTask) {
        return node;
      }
    }
  }
  return undefined;
}
function collectPeopleGroupNodes(flow: ApprovalTaskConfigFlow): Extract<ApprovalTaskConfigFlow, {
  type: 'peopleGroup';
}>[] {
  const nodes: Extract<ApprovalTaskConfigFlow, {
    type: 'peopleGroup';
  }>[] = [];
  function walk(node: ApprovalTaskConfigFlow): void {
    if (node.type === 'peopleGroup') {
      nodes.push(node);
    }
    for (const child of node.children ?? []) {
      walk(child);
    }
  }
  walk(flow);
  return nodes;
}

/** editingContent の approvalTask / approvalStages から ApprovalTaskConfigFlow を組み立てる。 */
export function toApprovalDraftFlow(content: DraftPatternSettingJson): ApprovalTaskConfigFlow | null {
  const approvalTask = findApprovalTaskNode(content)?.approvalTask;
  if (!approvalTask) return null;
  const stages = approvalTask.approvalStages ?? [];
  if (stages.length === 0) {
    return adjustFlowToStageCount(DEFAULT_APPROVAL_FLOW, 1);
  }
  const sorted = [...stages].sort((a, b) => a.stageIndex - b.stageIndex);
  const approverNodes: ApprovalTaskConfigFlow[] = sorted.map((stage, index) => ({
    id: `approver-${index + 1}`,
    type: 'peopleGroup',
    peopleGroup: stage.peopleGroupId ? {
      id: stage.peopleGroupId,
      name: stage.peopleGroupName ?? ''
    } : undefined,
    designationMode: stage.designationMode
  }));
  for (let index = 0; index < approverNodes.length; index++) {
    const current = approverNodes[index];
    if (!current) continue;
    const next = approverNodes[index + 1];
    current.children = next ? [next] : [{
      id: 'end',
      type: 'end'
    }];
  }
  const firstApprover = approverNodes[0];
  return {
    id: 'start',
    type: 'start',
    children: firstApprover !== undefined ? [firstApprover] : [{
      id: 'end',
      type: 'end'
    }]
  };
}

/** 承認フロー編集結果を editingContent の approvalStages へ反映する。 */
export function applyApprovalDraftToEditingContent(args: {
  base: DraftPatternSettingJson;
  flow: ApprovalTaskConfigFlow;
}): DraftPatternSettingJson {
  const next = structuredClone(args.base);
  const approvalTask = findApprovalTaskNode(next)?.approvalTask;
  if (!approvalTask) return next;
  const peopleGroupNodes = collectPeopleGroupNodes(args.flow);
  const stages = transformFlowToStages(args.flow);
  approvalTask.approvalStages = stages.map((stage, index) => {
    const node = peopleGroupNodes[index];
    return {
      stageIndex: stage.stageIndex,
      peopleGroupId: stage.peopleGroupId,
      peopleGroupName: node?.peopleGroup?.name,
      requiredApprovals: stage.requiredApprovals,
      designationMode: stage.designationMode
    };
  });

  // node レベルの単一値（BE publish 用 / DC-8217 A案）。現行エディタの承認は publish 時に
  // 単一の承認ノードへ写るため、代表値として先頭段の designationMode を採用する。
  // 多段で段ごとに異なる designationMode は node 単一値では表現しきれず、引き続き
  // 公開後の追いPUT（savePublishedApprovalFlow）経由で反映される。
  approvalTask.designationMode = stages[0]?.designationMode ?? 'none';
  return next;
}

/** 公開前に承認段階の担当グループがすべて設定されているか。 */
export function isApprovalDraftFlowValid(flow: ApprovalTaskConfigFlow): boolean {
  const peopleGroupNodes = collectPeopleGroupNodes(flow);
  if (peopleGroupNodes.length === 0) return false;
  return peopleGroupNodes.every(node => node.peopleGroup?.id && node.peopleGroup.id.length > 0);
}

/** draft status（4 値）を frontend の 2 値へ寄せる（Draft 以外は公開扱い）。 */
function toPatternDraftStatus(status: string): PackagePatternDraftStatus {
  return status === 'Draft' ? 'draft' : 'published';
}
function getFreeForm(content: DraftPatternSettingJson) {
  return findSubmitTaskWithFreeForm(content)?.freeForm;
}
function findSubmitTaskWithFreeForm(content: DraftPatternSettingJson): DraftNodeJson['submitTask'] | undefined {
  for (const step of content.steps ?? []) {
    for (const node of step.nodes ?? []) {
      if (node.submitTask?.freeForm) {
        return node.submitTask;
      }
    }
  }
  return undefined;
}

/** editingContent の freeForm.questions を PatternDraftQuestion[] へ変換する。 */
function toPatternDraftQuestions(content: DraftPatternSettingJson): PatternDraftQuestion[] {
  const freeForm = getFreeForm(content);
  if (!freeForm) return [];
  return mapJsonQuestionsToConfigQuestions(freeForm.questions);
}

/** getDraft のレスポンスから下書きエディタの初期 config を組み立てる。 */
export function toPackagePatternDraftConfig(args: {
  patternId: string;
  editingContent: DraftPatternSettingJson;
  status: string;
  packageDisplayName?: string;
}): PackagePatternDraftConfig {
  const {
    patternId,
    editingContent,
    status,
    packageDisplayName
  } = args;
  return {
    operationPackagePatternId: patternId,
    packageName: packageDisplayName ?? DEFAULT_IN_HOUSE_PACKAGE_DISPLAY_NAME,
    name: editingContent.name,
    description: editingContent.description ?? '',
    status: toPatternDraftStatus(status),
    createdBy: null,
    updatedBy: null,
    updatedAt: null,
    questions: toPatternDraftQuestions(editingContent)
  };
}

/**
 * 下書きエディタの編集結果を editingContent へ反映する（全体 PUT 用）。
 */
export function applyPackagePatternDraftToEditingContent(args: {
  base: DraftPatternSettingJson;
  config: Pick<PackagePatternDraftConfig, 'name' | 'description' | 'questions'>;
  approvalFlow?: ApprovalTaskConfigFlow;
}): DraftPatternSettingJson {
  const {
    base,
    config,
    approvalFlow
  } = args;
  const next = structuredClone(base);
  next.name = config.name;
  next.description = config.description;
  const submitTask = findSubmitTaskWithFreeForm(next);
  if (submitTask?.freeForm) {
    submitTask.freeForm.questions = mapConfigQuestionsToJson(config.questions);
  }
  if (approvalFlow && hasApprovalTaskInDraft(next)) {
    return applyApprovalDraftToEditingContent({
      base: next,
      flow: approvalFlow
    });
  }
  return next;
}